import { Box, Text } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

interface QrPositionPickerProps {
  baseImageUrl: string | null
  xPercent: number | null
  yPercent: number | null
  sizePercent: number | null
  minSizePercent: number
  onChange: (xPercent: number, yPercent: number, sizePercent: number) => void
  onInteracted?: () => void
}

type DragMode = 'move' | 'resize' | null

const DEFAULT_SIZE = 15
const DEFAULT_X = 70
const DEFAULT_Y = 70

/**
 * Lets advertisers drag a placeholder QR square over their uploaded image and
 * resize it. Values are stored as percentages of the image so the server can
 * composite at full resolution. Pointer events only — no extra deps.
 */
export const QrPositionPicker = ({
  baseImageUrl,
  xPercent,
  yPercent,
  sizePercent,
  minSizePercent,
  onChange,
  onInteracted,
}: QrPositionPickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dragMode, setDragMode] = useState<DragMode>(null)
  const [dragOrigin, setDragOrigin] = useState<{
    pointerX: number
    pointerY: number
    x: number
    y: number
    size: number
  } | null>(null)

  // Initialize to defaults if not set yet and we have an image.
  // onChange is intentionally excluded from deps — including it would re-fire
  // on every render if the parent doesn't memoize the callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      baseImageUrl &&
      (xPercent == null || yPercent == null || sizePercent == null)
    ) {
      onChange(DEFAULT_X, DEFAULT_Y, DEFAULT_SIZE)
    }
  }, [baseImageUrl, xPercent, yPercent, sizePercent])

  const x = xPercent ?? DEFAULT_X
  const y = yPercent ?? DEFAULT_Y
  const size = sizePercent ?? DEFAULT_SIZE

  const clamp = (v: number, min: number, max: number) =>
    Math.min(Math.max(v, min), max)

  const handlePointerDown = (mode: DragMode) => (e: React.PointerEvent) => {
    e.stopPropagation()
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    setDragMode(mode)
    setDragOrigin({ pointerX: e.clientX, pointerY: e.clientY, x, y, size })
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragMode || !dragOrigin || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const dxPct = ((e.clientX - dragOrigin.pointerX) / rect.width) * 100
    const dyPct = ((e.clientY - dragOrigin.pointerY) / rect.height) * 100

    if (dragMode === 'move') {
      const newX = clamp(dragOrigin.x + dxPct, 0, 100 - size)
      const newY = clamp(dragOrigin.y + dyPct, 0, 100 - size)
      onChange(round(newX), round(newY), round(size))
    } else if (dragMode === 'resize') {
      // Resize from top-left corner fixed; grow to bottom-right via dx+dy avg.
      const delta = (dxPct + dyPct) / 2
      const newSize = clamp(
        dragOrigin.size + delta,
        minSizePercent,
        Math.min(100 - dragOrigin.x, 100 - dragOrigin.y),
      )
      onChange(round(dragOrigin.x), round(dragOrigin.y), round(newSize))
    }
  }

  const handlePointerUp = () => {
    if (dragMode !== null) onInteracted?.()
    setDragMode(null)
    setDragOrigin(null)
  }

  if (!baseImageUrl) {
    return (
      <Box
        p='md'
        style={{
          border: '1px dashed #ccc',
          borderRadius: 8,
          textAlign: 'center',
        }}
      >
        <Text c='dimmed' size='sm'>
          Upload your ad image first — then drag the QR placeholder to position
          it.
        </Text>
      </Box>
    )
  }

  return (
    <Box
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 640,
        margin: '0 auto',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <img
        src={baseImageUrl}
        alt='ad preview'
        style={{
          width: '100%',
          display: 'block',
          borderRadius: 8,
          pointerEvents: 'none',
        }}
      />
      <Box
        onPointerDown={handlePointerDown('move')}
        style={{
          position: 'absolute',
          left: `${x}%`,
          top: `${y}%`,
          width: `${size}%`,
          aspectRatio: '1 / 1',
          background:
            'repeating-linear-gradient(45deg, #000 0 6px, #fff 6px 12px)',
          border: '2px solid #1971c2',
          cursor: dragMode === 'move' ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 0 2px rgba(25,113,194,0.25)',
        }}
      >
        <Text size='xs' c='white' fw={700} style={{ mixBlendMode: 'difference' }}>
          QR
        </Text>
        <Box
          onPointerDown={handlePointerDown('resize')}
          style={{
            position: 'absolute',
            right: -8,
            bottom: -8,
            width: 16,
            height: 16,
            background: '#1971c2',
            borderRadius: 4,
            cursor: 'nwse-resize',
          }}
        />
      </Box>
    </Box>
  )
}

const round = (n: number) => Math.round(n * 1000) / 1000
